import { log } from '../../utils';
import { GLPrecision } from '../types';
import { GLProbe } from './GLProbe';

type AnyWebGL = WebGLRenderingContext | WebGL2RenderingContext;

/**
 * Lazy initialize WebGL constants
 */
export class WebGLProbe extends GLProbe {
  private getContext(canvas: HTMLCanvasElement): AnyWebGL | null {
    const gl2 = canvas.getContext('webgl2') as WebGL2RenderingContext | null;
    if (gl2) {
      this.version = 2;
      return gl2;
    }

    const gl1 = canvas.getContext('webgl') as WebGLRenderingContext | null;
    if (gl1) {
      this.version = 1;
      return gl1;
    }

    this.version = undefined;
    return null;
  }

  /**
   * Tests if webgl supports certain precision
   * @param {WebGL} Canvas WebGL context to test on
   * @param {GLPrecision} Precision to test can be any of following
   * @returns {Boolean} Whether the user's browser WebGL supports given precision.
   */
  private testPrecision(gl: AnyWebGL, precision: GLPrecision): boolean {
    const fragmentSource = `precision ${precision} float;\nvoid main(){}`;
    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    if (!fragmentShader) return false;

    gl.shaderSource(fragmentShader, fragmentSource);
    gl.compileShader(fragmentShader);

    const hasShaderParameter = !!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS);
    gl.deleteShader(fragmentShader);
    return hasShaderParameter;
  }

  /**
   * query browser for WebGL
   */
  queryWebGL(canvas?: HTMLCanvasElement): void {
    if (!canvas) return;

    const gl = this.getContext(canvas);
    if (!gl) return;

    this.maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);

    this.precision = (['highp', 'mediump', 'lowp'] as const).find(precision => this.testPrecision(gl, precision));

    // Best-effort context release (also available in webgl2 if the extension exists)
    gl.getExtension('WEBGL_lose_context')?.loseContext();

    log('log', `WebGL: max texture size ${this.maxTextureSize}`);
  }

  isSupported(textureSize: number): boolean {
    return !!this.maxTextureSize && this.maxTextureSize >= textureSize;
  }
}
